import type { AgentBlocksText } from '@fluenceur/agent-blocks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The dashboard's words for the companion blocks (`@fluenceur/agent-blocks`),
 * read from `companion.agent.*`. Every key is written out in full so the i18n
 * orphan check can see it.
 */
export function useAgentBlocksText(): AgentBlocksText {
  // react-i18next hands a new `t` on every language change, which is what
  // rebuilds the words.
  const { t } = useTranslation();

  return useMemo<AgentBlocksText>(
    () => ({
      header: {
        newConversation: t('companion.agent.header.newConversation'),
        close: t('companion.agent.header.close'),
        back: t('companion.agent.header.back'),
      },
      home: {
        greeting: (firstName) =>
          firstName
            ? t('companion.agent.home.greeting', { firstName })
            : t('companion.agent.home.greetingAnonymous'),
        pendingSummary: (count) => t('companion.agent.home.pendingSummary', { count }),
        allClear: t('companion.agent.home.allClear'),
        toDoTitle: t('companion.agent.home.toDoTitle'),
        seeAll: (count) => t('companion.agent.home.seeAll', { count }),
        ignore: t('companion.agent.home.ignore'),
        ignoreItem: (title) => t('companion.agent.home.ignoreItem', { title }),
        suggestionsLabel: t('companion.agent.home.suggestionsLabel'),
      },
      trace: {
        runningFallback: t('companion.agent.trace.runningFallback'),
        done: (seconds, sources) => t('companion.agent.trace.done', { seconds, count: sources }),
        unfinished: (steps) => t('companion.agent.trace.unfinished', { count: steps }),
        stepsLabel: t('companion.agent.trace.stepsLabel'),
        unfinishedStepsLabel: t('companion.agent.trace.unfinishedStepsLabel'),
      },
      answer: {
        ready: t('companion.agent.answer.ready'),
        copy: t('companion.agent.answer.copy'),
        copied: t('companion.agent.answer.copied'),
        helpful: t('companion.agent.answer.helpful'),
        notHelpful: t('companion.agent.answer.notHelpful'),
        sourcesLabel: t('companion.agent.answer.sourcesLabel'),
      },
      period: {
        label: t('companion.agent.period.label'),
      },
      definition: {
        show: (term) => t('companion.agent.definition.show', { term }),
        hide: (term) => t('companion.agent.definition.hide', { term }),
      },
      metrics: {
        unavailable: t('companion.agent.metrics.unavailable'),
      },
      table: {
        moreInFile: (count) => t('companion.agent.table.moreInFile', { count }),
      },
      file: {
        preparing: t('companion.agent.file.preparing'),
        download: t('companion.agent.file.download'),
        expired: t('companion.agent.file.expired'),
        redo: t('companion.agent.file.redo'),
        failed: t('companion.agent.file.failed'),
        retry: t('companion.agent.file.retry'),
      },
      shortlist: {
        label: t('companion.agent.shortlist.label'),
        tier: {
          excellent: t('companion.agent.shortlist.tier.excellent'),
          good: t('companion.agent.shortlist.tier.good'),
          moderate: t('companion.agent.shortlist.tier.moderate'),
        },
        propose: (count) => t('companion.agent.shortlist.propose', { count }),
      },
      draft: {
        channel: {
          email: t('companion.agent.draft.channel.email'),
          grouped_email: t('companion.agent.draft.channel.grouped_email'),
          rights_proposal: t('companion.agent.draft.channel.rights_proposal'),
          instagram_dm: t('companion.agent.draft.channel.instagram_dm'),
          tiktok_dm: t('companion.agent.draft.channel.tiktok_dm'),
        },
        recipients: (count) => t('companion.agent.draft.recipients', { count }),
        badgeDraft: t('companion.agent.draft.badgeDraft'),
        badgeExpiringSoon: t('companion.agent.draft.badgeExpiringSoon'),
        subjectLabel: t('companion.agent.draft.subjectLabel'),
        bodyLabel: t('companion.agent.draft.bodyLabel'),
        open: t('companion.agent.draft.open'),
        ignore: t('companion.agent.draft.ignore'),
        copyAndOpen: {
          instagram_dm: t('companion.agent.draft.copyAndOpen.instagram_dm'),
          tiktok_dm: t('companion.agent.draft.copyAndOpen.tiktok_dm'),
        },
      },
      execution: {
        scheduled: (seconds) => t('companion.agent.execution.scheduled', { seconds }),
        cancelSending: t('companion.agent.execution.cancelSending'),
        sending: t('companion.agent.execution.sending'),
        sentTo: (handle) => t('companion.agent.execution.sentTo', { handle }),
        view: t('companion.agent.execution.view'),
        failed: t('companion.agent.execution.failed'),
        retry: t('companion.agent.execution.retry'),
        stale: t('companion.agent.execution.stale'),
        update: t('companion.agent.execution.update'),
        notExecutable: t('companion.agent.execution.notExecutable'),
        openPage: t('companion.agent.execution.openPage'),
        ignored: t('companion.agent.execution.ignored'),
        undo: t('companion.agent.execution.undo'),
        dmAwaiting: {
          instagram_dm: t('companion.agent.execution.dmAwaiting.instagram_dm'),
          tiktok_dm: t('companion.agent.execution.dmAwaiting.tiktok_dm'),
        },
        dmDeclareSent: t('companion.agent.execution.dmDeclareSent'),
        dmNotYet: t('companion.agent.execution.dmNotYet'),
        dmDeclared: t('companion.agent.execution.dmDeclared'),
      },
      followUps: {
        label: t('companion.agent.followUps.label'),
      },
      composer: {
        label: t('companion.agent.composer.label'),
        placeholder: t('companion.agent.composer.placeholder'),
        footer: t('companion.agent.composer.footer'),
        send: t('companion.agent.composer.send'),
        stop: t('companion.agent.composer.stop'),
        counter: (length, max) => t('companion.agent.composer.counter', { length, max }),
        page: (name) => t('companion.agent.composer.page', { name }),
      },
    }),
    [t]
  );
}
